import React, { useState, useEffect, useRef } from "react";
import { useTasks } from "../context/TaskContext";
import { useProjects } from "../context/ProjectContext";
import { getLocalDateString } from "../utils/dateUtils";

import { SchedulePicker } from "./tasks/selectors/SchedulePicker";
import { PriorityPicker } from "./tasks/selectors/PriorityPicker";
import { ReminderPicker } from "./tasks/selectors/ReminderPicker";
import { ProjectPicker } from "./tasks/selectors/ProjectPicker";

export const TaskForm = ({ isOpen, onClose }) => {
    const { creatTasks } = useTasks();
    const { projects } = useProjects();

    // Estados de los datos de la tarea
    const [taskName, setTaskName] = useState("");
    const [description, setDescription] = useState("");
    const [projectId, setProjectId] = useState("inbox");
    const [priority, setPriority] = useState(4);

    const [dueDate, setDueDate] = useState(getLocalDateString());
    const [dueTime, setDueTime] = useState("");
    const [isRecurring, setIsRecurring] = useState(false);
    const [recurrenceType, setRecurrenceType] = useState("daily");
    const [recurrenceDays, setRecurrenceDays] = useState([]);

    const [reminderType, setReminderType] = useState("none");
    const [reminderDate, setReminderDate] = useState("");

    // Estado para prevenir doble clic / autoclick repetitivo
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Estados para controlar los menús desplegables
    const [showSchedule, setShowSchedule] = useState(false);
    const [showPriority, setShowPriority] = useState(false);
    const [showReminder, setShowReminder] = useState(false);

    const scheduleRef = useRef(null);
    const priorityRef = useRef(null);
    const reminderRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (scheduleRef.current && !scheduleRef.current.contains(event.target)) {
                setShowSchedule(false);
            }
            if (priorityRef.current && !priorityRef.current.contains(event.target)) {
                setShowPriority(false);
            }
            if (reminderRef.current && !reminderRef.current.contains(event.target)) {
                setShowReminder(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!taskName.trim() || isSubmitting) return;

        setIsSubmitting(true);

        try {
            const taskPayload = {
                name: taskName.trim(),
                description: description.trim() || null,
                project_id: projectId === "inbox" ? null : Number(projectId),
                priority: priority,
                due_date: dueDate,
                due_time: dueTime || null,
                is_recurring: isRecurring,
                recurrence_type: isRecurring ? recurrenceType : null,
                recurrence_days: isRecurring && recurrenceType === "custom" ? recurrenceDays : null,
                reminder_type: reminderType,
                reminder_date: reminderType === "custom" ? reminderDate : null
            };

            await creatTasks(taskPayload);

            // Resetear todos los estados
            setTaskName("");
            setDescription("");
            setProjectId("inbox");
            setPriority(4);
            setDueDate(getLocalDateString());
            setDueTime("");
            setIsRecurring(false);
            setRecurrenceType("daily");
            setRecurrenceDays([]);
            setReminderType("none");
            setReminderDate("");
            setShowSchedule(false);
            setShowPriority(false);
            setShowReminder(false);
            onClose();
        } catch (error) {
            console.error("Error al crear la tarea:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Fondo oscuro */}
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}></div>

            {/* Contenedor del Modal */}
            <div className="bg-white rounded-xl shadow-2xl border border-slate-100 max-w-lg w-full p-5 relative z-10">
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Input: Nombre de la tarea */}
                    <div className="flex items-center justify-between">
                        <input
                            type="text"
                            placeholder="Nombre de la tarea"
                            value={taskName}
                            onChange={(e) => setTaskName(e.target.value)}
                            className="w-full text-base font-medium text-gray-800 placeholder-gray-400 focus:outline-none"
                            maxLength={100}
                            autoFocus
                        />
                        {taskName.trim() && (
                            <span className="flex items-center space-x-1 text-xs text-emerald-600 font-semibold bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200 animate-fadeIn flex-shrink-0 ml-2">
                                <i className="fas fa-check text-[10px]"></i>
                                <span>Listo</span>
                            </span>
                        )}
                    </div>

                    {/* Textarea: Descripción corta */}
                    <div className="relative">
                        <textarea
                            placeholder="Descripción"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="w-full text-sm text-gray-600 placeholder-gray-400 focus:outline-none h-12 resize-none pr-16"
                        />
                        {description.trim() && (
                            <span className="absolute right-0 bottom-2 flex items-center space-x-1 text-[10px] text-emerald-600 font-semibold bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200 animate-fadeIn">
                                <i className="fas fa-check text-[9px]"></i>
                                <span>Detalle ok</span>
                            </span>
                        )}
                    </div>

                    {/* Fila de Iconos de Configuración Rápida */}
                    <div className="flex items-center space-x-2 pt-1">
                        <SchedulePicker
                            dueDate={dueDate}
                            setDueDate={setDueDate}
                            dueTime={dueTime}
                            setDueTime={setDueTime}
                            isRecurring={isRecurring}
                            setIsRecurring={setIsRecurring}
                            recurrenceType={recurrenceType}
                            setRecurrenceType={setRecurrenceType}
                            recurrenceDays={recurrenceDays}
                            setRecurrenceDays={setRecurrenceDays}
                            showSchedule={showSchedule}
                            setShowSchedule={setShowSchedule}
                            scheduleRef={scheduleRef}
                        />

                        <PriorityPicker
                            priority={priority}
                            setPriority={setPriority}
                            showPriority={showPriority}
                            setShowPriority={setShowPriority}
                            priorityRef={priorityRef}
                        />

                        <ReminderPicker
                            reminderType={reminderType}
                            setReminderType={setReminderType}
                            reminderDate={reminderDate}
                            setReminderDate={setReminderDate}
                            showReminder={showReminder}
                            setShowReminder={setShowReminder}
                            reminderRef={reminderRef}
                        />
                    </div>

                    {/* Separador con Selector de Destino y Botones */}
                    <div className="border-t border-gray-100 pt-3 flex items-center justify-between">
                        <ProjectPicker
                            projects={projects}
                            projectId={projectId}
                            setProjectId={setProjectId}
                        />

                        <div className="flex space-x-2">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-1.5 text-xs font-semibold text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting || !taskName.trim()}
                                className={`px-4 py-1.5 text-xs font-semibold text-white rounded-lg shadow-md transition-all flex items-center space-x-1.5 ${
                                    isSubmitting || !taskName.trim()
                                        ? "bg-blue-400 cursor-not-allowed opacity-75"
                                        : "bg-blue-600 hover:bg-blue-700 active:scale-95"
                                }`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin text-xs"></i>
                                        <span>Creando...</span>
                                    </>
                                ) : (
                                    <span>Añadir tarea</span>
                                )}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};